import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronRight,
  ChevronLeft,
  Users,
  Bot,
  Smartphone,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Info,
  Coins,
  Clock,
  Target,
  Zap
} from 'lucide-react';
import { advertiserApi, CreateAdRequest } from '../../api';
import { useTelegram } from '../../hooks';

interface AdConfig {
  platformFeePercent: number;
  verificationTimeFeePerSecond: number;
}

interface CreateAdWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  balance: number;
}

type AdType = 'telegram_channel' | 'telegram_group' | 'telegram_bot' | 'telegram_miniapp';

interface FormData {
  type: AdType;
  title: string;
  description: string;
  targetUrl: string;
  // Telegram-specific fields
  channelUsername: string;
  groupUsername: string;
  botUsername: string;
  botDeepLink: string;
  miniAppUrl: string;
  rewardPerCompletion: number;
  targetCompletions: number;
  verificationTime: number;
  dailyLimit: number;
  isBoosted: boolean;
}

const AD_TYPES = [
  { id: 'telegram_channel' as AdType, icon: Users, label: 'Telegram Channel', description: 'Get members to join your channel' },
  { id: 'telegram_group' as AdType, icon: Users, label: 'Telegram Group', description: 'Get members to join your group' },
  { id: 'telegram_bot' as AdType, icon: Bot, label: 'Telegram Bot', description: 'Get users to start your bot' },
  { id: 'telegram_miniapp' as AdType, icon: Smartphone, label: 'Mini App', description: 'Get users to open your mini app' },
];

const INITIAL_FORM: FormData = {
  type: 'telegram_channel',
  title: '',
  description: '',
  targetUrl: '',
  channelUsername: '',
  groupUsername: '',
  botUsername: '',
  botDeepLink: '',
  miniAppUrl: '',
  rewardPerCompletion: 10,
  targetCompletions: 100,
  verificationTime: 30,
  dailyLimit: 1000,
  isBoosted: false,
};

export function CreateAdWizard({ isOpen, onClose, onSuccess, balance }: CreateAdWizardProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adConfig, setAdConfig] = useState<AdConfig>({
    platformFeePercent: 10,
    verificationTimeFeePerSecond: 0.1,
  });

  const { hapticFeedback, hapticNotification } = useTelegram();

  const totalSteps = 4;

  // Fetch ad config on mount
  useEffect(() => {
    if (isOpen) {
      advertiserApi.getConfig().then(response => {
        if (response.success && response.config) {
          setAdConfig({
            platformFeePercent: response.config.platformFeePercent || 10,
            verificationTimeFeePerSecond: (response.config as any).verificationTimeFeePerSecond || 0.1,
          });
        }
      }).catch(err => {
        console.error('Failed to fetch ad config:', err);
      });
    }
  }, [isOpen]);

  const updateForm = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const calculateCost = () => {
    const baseCost = formData.rewardPerCompletion * formData.targetCompletions;
    const platformFee = Math.ceil(baseCost * (adConfig.platformFeePercent / 100));
    const verificationTimeFee = Math.ceil(formData.verificationTime * adConfig.verificationTimeFeePerSecond * formData.targetCompletions);
    const boostFee = formData.isBoosted ? Math.ceil(baseCost * 0.2) : 0; // 20% boost fee
    return baseCost + platformFee + verificationTimeFee + boostFee;
  };

  const handleNext = () => {
    hapticFeedback('light');
    
    // Validation per step
    if (step === 1 && !formData.type) {
      setError('Please select an ad type');
      return;
    }
    
    if (step === 2) {
      if (!formData.title.trim()) {
        setError('Please enter a title');
        return;
      }
      if (!formData.description.trim()) {
        setError('Please enter a description');
        return;
      }
      if (formData.type === 'telegram_channel' && !formData.channelUsername.trim()) {
        setError('Please enter the channel username');
        return;
      }
      if (formData.type === 'telegram_group' && !formData.groupUsername.trim()) {
        setError('Please enter the group username');
        return;
      }
      if (formData.type === 'telegram_bot' && !formData.botUsername.trim()) {
        setError('Please enter the bot username');
        return;
      }
      if (formData.type === 'telegram_miniapp' && !formData.miniAppUrl.trim()) {
        setError('Please enter the Mini App URL');
        return;
      }
    }
    
    if (step === 3) {
      if (formData.rewardPerCompletion < 5) {
        setError('Minimum reward is 5 points');
        return;
      }
      if (formData.targetCompletions < 10) {
        setError('Minimum target is 10 completions');
        return;
      }
      
      const cost = calculateCost();
      if (cost > balance) {
        setError('Insufficient balance. Please add funds first.');
        return;
      }
    }

    setError(null);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    hapticFeedback('light');
    setError(null);
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    hapticFeedback('medium');
    setLoading(true);
    setError(null);

    try {
      // Build type-specific fields
      let typeFields: Partial<CreateAdRequest> = {};
      
      switch (formData.type) {
        case 'telegram_channel':
          typeFields = { telegramUsername: formData.channelUsername };
          break;
        case 'telegram_group':
          typeFields = { telegramUsername: formData.groupUsername };
          break;
        case 'telegram_bot':
          typeFields = { 
            telegramUsername: formData.botUsername,
            botDeepLink: formData.botDeepLink || undefined,
          };
          break;
        case 'telegram_miniapp':
          typeFields = { miniAppUrl: formData.miniAppUrl };
          break;
      }

      const request: CreateAdRequest = {
        type: formData.type,
        title: formData.title,
        description: formData.description,
        rewardPerCompletion: formData.rewardPerCompletion,
        targetCompletions: formData.targetCompletions,
        dailyLimit: formData.dailyLimit,
        ...typeFields,
      };

      const response = await advertiserApi.createAd(request);

      if (response.success) {
        hapticNotification('success');
        setFormData(INITIAL_FORM);
        setStep(1);
        onSuccess();
      } else {
        setError(response.message || 'Failed to create ad');
        hapticNotification('error');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create ad');
      hapticNotification('error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(INITIAL_FORM);
    setStep(1);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-0 left-0 right-0 z-50 max-h-[85vh] overflow-y-auto pb-20"
          >
            <div className="bg-surface rounded-t-3xl p-6 pb-24">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Create New Ad</h2>
                  <p className="text-white/60 text-sm">Step {step} of {totalSteps}</p>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20"
                >
                  <X className="w-5 h-5 text-white/70" />
                </button>
              </div>

              {/* Progress Bar */}
              <div className="h-1.5 bg-white/10 rounded-full mb-6 overflow-hidden">
                <motion.div
                  className="h-full bg-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${(step / totalSteps) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              {/* Step Content */}
              <AnimatePresence mode="wait">
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-3"
                  >
                    <h3 className="font-semibold text-white mb-4">Select Ad Type</h3>
                    {AD_TYPES.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => {
                          hapticFeedback('light');
                          updateForm({ type: type.id });
                        }}
                        className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all ${
                          formData.type === type.id
                            ? 'bg-primary/20 border-2 border-primary'
                            : 'bg-white/10 border-2 border-transparent hover:bg-white/20'
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl ${formData.type === type.id ? 'bg-primary' : 'bg-white/20'} flex items-center justify-center`}>
                          <type.icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-left flex-1">
                          <p className="font-semibold text-white">{type.label}</p>
                          <p className="text-sm text-white/60">{type.description}</p>
                        </div>
                        {formData.type === type.id && (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}

                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <h3 className="font-semibold text-white mb-4">Ad Details</h3>
                    
                    <div>
                      <label className="block text-sm text-white/60 mb-2">Title</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => updateForm({ title: e.target.value })}
                        placeholder="e.g., Join our crypto community"
                        maxLength={50}
                        className="w-full px-4 py-3 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <p className="text-xs text-white/40 mt-1">{formData.title.length}/50</p>
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => updateForm({ description: e.target.value })}
                        placeholder="Brief description of what users will do..."
                        maxLength={150}
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                      />
                      <p className="text-xs text-white/40 mt-1">{formData.description.length}/150</p>
                    </div>

                    {formData.type === 'telegram_channel' && (
                      <div>
                        <label className="block text-sm text-white/60 mb-2">Channel Username</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">@</span>
                          <input
                            type="text"
                            value={formData.channelUsername}
                            onChange={(e) => updateForm({ channelUsername: e.target.value.replace('@', '') })}
                            placeholder="yourchannel"
                            className="w-full px-4 py-3 pl-8 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                        <p className="text-xs text-white/40 mt-1">Enter your public channel username</p>
                        <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                          <p className="text-xs text-yellow-400 font-medium mb-1">⚠️ Important Setup Step</p>
                          <p className="text-xs text-yellow-300/80">
                            Add <span className="font-mono font-semibold">@GemiFaucetBot</span> as an admin to your channel. 
                            This allows us to verify that users have joined. Without this, ad creation will fail.
                          </p>
                        </div>
                      </div>
                    )}

                    {formData.type === 'telegram_group' && (
                      <div>
                        <label className="block text-sm text-white/60 mb-2">Group Username</label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">@</span>
                          <input
                            type="text"
                            value={formData.groupUsername}
                            onChange={(e) => updateForm({ groupUsername: e.target.value.replace('@', '') })}
                            placeholder="yourgroup"
                            className="w-full px-4 py-3 pl-8 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                        </div>
                        <p className="text-xs text-white/40 mt-1">Enter your public group username</p>
                        <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                          <p className="text-xs text-yellow-400 font-medium mb-1">⚠️ Important Setup Step</p>
                          <p className="text-xs text-yellow-300/80">
                            Add <span className="font-mono font-semibold">@GemiFaucetBot</span> as an admin to your group. 
                            This allows us to verify that users have joined. Without this, ad creation will fail.
                          </p>
                        </div>
                      </div>
                    )}

                    {formData.type === 'telegram_bot' && (
                      <>
                        <div>
                          <label className="block text-sm text-white/60 mb-2">Bot Username</label>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40">@</span>
                            <input
                              type="text"
                              value={formData.botUsername}
                              onChange={(e) => updateForm({ botUsername: e.target.value.replace('@', '') })}
                              placeholder="yourbot"
                              className="w-full px-4 py-3 pl-8 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-white/60 mb-2">Start Parameter (Optional)</label>
                          <input
                            type="text"
                            value={formData.botDeepLink}
                            onChange={(e) => updateForm({ botDeepLink: e.target.value })}
                            placeholder="ref123"
                            className="w-full px-4 py-3 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                          <p className="text-xs text-white/40 mt-1">Deep link parameter for ?start=</p>
                        </div>
                      </>
                    )}

                    {formData.type === 'telegram_miniapp' && (
                      <div>
                        <label className="block text-sm text-white/60 mb-2">Mini App URL</label>
                        <input
                          type="url"
                          value={formData.miniAppUrl}
                          onChange={(e) => updateForm({ miniAppUrl: e.target.value })}
                          placeholder="https://t.me/appname"
                          className="w-full px-4 py-3 rounded-xl bg-white/10 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                        <p className="text-xs text-white/40 mt-1">Full URL to your mini app</p>
                      </div>
                    )}
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <h3 className="font-semibold text-white mb-4">Campaign Settings</h3>
                    
                    <div>
                      <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                        <Coins className="w-4 h-4" />
                        Reward per Completion
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="5"
                          max="100"
                          step="5"
                          value={formData.rewardPerCompletion}
                          onChange={(e) => updateForm({ rewardPerCompletion: parseInt(e.target.value) })}
                          className="flex-1 accent-primary"
                        />
                        <span className="w-16 text-center font-semibold text-white bg-white/10 rounded-lg py-2">
                          {formData.rewardPerCompletion}
                        </span>
                      </div>
                      <p className="text-xs text-white/40 mt-1">Higher rewards attract more users</p>
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Target Completions
                      </label>
                      <input
                        type="number"
                        min="10"
                        value={formData.targetCompletions}
                        onChange={(e) => updateForm({ targetCompletions: parseInt(e.target.value) || 10 })}
                        className="w-full px-4 py-3 rounded-xl bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-white/60 mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Verification Time (seconds)
                      </label>
                      <select
                        value={formData.verificationTime}
                        onChange={(e) => updateForm({ verificationTime: parseInt(e.target.value) })}
                        className="w-full px-4 py-3 rounded-xl bg-white/10 text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
                      >
                        <option value="15">15 seconds</option>
                        <option value="30">30 seconds</option>
                        <option value="60">60 seconds</option>
                        <option value="120">2 minutes</option>
                      </select>
                    </div>

                    {/* Boost Option */}
                    <button
                      onClick={() => updateForm({ isBoosted: !formData.isBoosted })}
                      className={`w-full p-4 rounded-xl flex items-center gap-4 transition-all ${
                        formData.isBoosted
                          ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-2 border-yellow-500'
                          : 'bg-white/10 border-2 border-transparent'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg ${formData.isBoosted ? 'bg-yellow-500' : 'bg-white/20'} flex items-center justify-center`}>
                        <Zap className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-semibold text-white">Boost Ad ⚡</p>
                        <p className="text-sm text-white/60">+20% fee, priority placement</p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 ${formData.isBoosted ? 'bg-yellow-500 border-yellow-500' : 'border-white/30'} flex items-center justify-center`}>
                        {formData.isBoosted && <CheckCircle2 className="w-4 h-4 text-white" />}
                      </div>
                    </button>
                  </motion.div>
                )}

                {step === 4 && (
                  <motion.div
                    key="step4"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-4"
                  >
                    <h3 className="font-semibold text-white mb-4">Review & Confirm</h3>
                    
                    {/* Summary */}
                    <div className="bg-white/5 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Type</span>
                        <span className="text-white capitalize">{formData.type.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Title</span>
                        <span className="text-white">{formData.title}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Reward</span>
                        <span className="text-white">{formData.rewardPerCompletion} points</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Target</span>
                        <span className="text-white">{formData.targetCompletions} completions</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-white/60">Verification</span>
                        <span className="text-white">{formData.verificationTime}s</span>
                      </div>
                      {formData.isBoosted && (
                        <div className="flex justify-between">
                          <span className="text-white/60">Boost</span>
                          <span className="text-yellow-400">⚡ Enabled</span>
                        </div>
                      )}
                    </div>

                    {/* Cost Breakdown */}
                    <div className="bg-primary/10 border border-primary/30 rounded-xl p-4">
                      <h4 className="font-medium text-white mb-3">Cost Breakdown</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/60">Base cost</span>
                          <span className="text-white">
                            {(formData.rewardPerCompletion * formData.targetCompletions).toLocaleString()} pts
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/60">Platform fee (10%)</span>
                          <span className="text-white">
                            {Math.ceil(formData.rewardPerCompletion * formData.targetCompletions * 0.1).toLocaleString()} pts
                          </span>
                        </div>
                        {formData.isBoosted && (
                          <div className="flex justify-between">
                            <span className="text-white/60">Boost fee (20%)</span>
                            <span className="text-yellow-400">
                              {Math.ceil(formData.rewardPerCompletion * formData.targetCompletions * 0.2).toLocaleString()} pts
                            </span>
                          </div>
                        )}
                        <div className="border-t border-white/10 pt-2 flex justify-between font-semibold">
                          <span className="text-white">Total</span>
                          <span className="text-primary">{calculateCost().toLocaleString()} pts</span>
                        </div>
                      </div>
                    </div>

                    {/* Balance Check */}
                    <div className={`p-4 rounded-xl flex items-center gap-3 ${
                      calculateCost() <= balance 
                        ? 'bg-green-500/10 border border-green-500/30' 
                        : 'bg-red-500/10 border border-red-500/30'
                    }`}>
                      {calculateCost() <= balance ? (
                        <>
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                          <div>
                            <p className="text-green-400 font-medium">Sufficient Balance</p>
                            <p className="text-sm text-green-300/70">
                              Your balance: {balance.toLocaleString()} pts
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-5 h-5 text-red-400" />
                          <div>
                            <p className="text-red-400 font-medium">Insufficient Balance</p>
                            <p className="text-sm text-red-300/70">
                              Need {(calculateCost() - balance).toLocaleString()} more points
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Info */}
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
                      <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-blue-400 font-medium">Review Process</p>
                        <p className="text-sm text-blue-300/70">
                          Your ad will be reviewed by our team within 24 hours before going live.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error Display */}
              {error && (
                <div className="mt-4 bg-red-500/20 border border-red-500/50 rounded-xl p-3 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex gap-3 mt-6">
                {step > 1 && (
                  <button
                    onClick={handleBack}
                    className="flex-1 py-4 rounded-xl bg-white/10 text-white font-medium flex items-center justify-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back
                  </button>
                )}
                {step < totalSteps ? (
                  <button
                    onClick={handleNext}
                    className="flex-1 py-4 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2"
                  >
                    Next
                    <ChevronRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading || calculateCost() > balance}
                    className="flex-1 py-4 rounded-xl bg-gradient-to-r from-primary to-secondary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-5 h-5" />
                        Create Ad
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
